import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Box, Typography, Grid, Card, CardContent, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, useTheme, useMediaQuery } from '@mui/material';
import { Add } from '@mui/icons-material';
import { paymentsAPI } from '../services/api';

const safeArray = (val) => Array.isArray(val) ? val : [];

// Razorpay script loader
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) return resolve(true);
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Payments = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    flatId: '',
    amount: '',
    paymentType: '',
    paymentMethod: '',
    status: 'PENDING',
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  const { data: payments = [], isLoading } = useQuery('payments', async () => (await paymentsAPI.getAll()).data);

  const createMutation = useMutation(paymentsAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('payments');
      handleClose();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to create payment');
    },
  });

  const updateMutation = useMutation(
    ({ paymentId, data }) => paymentsAPI.update(paymentId, data),
    {
      onSuccess: () => queryClient.invalidateQueries('payments'),
      onError: (error) => setError(error.response?.data?.message || 'Failed to update payment'),
    }
  );

  const handleOpen = () => {
    setFormData({ flatId: '', amount: '', paymentType: '', paymentMethod: '', status: 'PENDING' });
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ flatId: '', amount: '', paymentType: '', paymentMethod: '', status: 'PENDING' });
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  // Razorpay payment handler
  const handlePayNow = async (payment) => {
    const res = await loadRazorpayScript();
    if (!res) {
      console.log('Razorpay SDK failed to load.');
      return;
    }

    // Call backend to create Razorpay order
    const order = await paymentsAPI.createRazorpayOrder({
      amount: payment.amount * 100, // Razorpay expects amount in paise
      currency: 'INR',
      receipt: `receipt_${payment.paymentId}`,
      paymentId: payment.paymentId,
    });

    const options = {
      key: 'YOUR_RAZORPAY_KEY', // Replace with your Razorpay key
      amount: order.amount,
      currency: order.currency,
      name: 'Apartment Management',
      description: `Payment for Flat ${payment.flatId}`,
      order_id: order.id,
      handler: function (response) {
        // Update payment status on success
        updateMutation.mutate({
          paymentId: payment.paymentId,
          data: { status: 'COMPLETED', razorpayPaymentId: response.razorpay_payment_id }
        });
      },
      prefill: {
        email: payment.email || '',
      },
      theme: { color: '#1976d2' }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  if (payments && payments.message) {
    return <Typography color="error">{payments.message}</Typography>;
  }
  const paymentsSafe = safeArray(payments);

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PENDING': return 'warning';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant={isMobile ? "h5" : "h4"}>{t('payments.title')}</Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={handleOpen}
          sx={{ minHeight: isMobile ? 44 : 'auto', fontSize: isMobile ? '14px' : '16px' }}
        >
          {t('payments.makePayment')}
        </Button>
      </Box>
      {isLoading ? (
        <Typography>{t('common.loading')}</Typography>
      ) : paymentsSafe.length === 0 ? (
        <Typography>{t('messages.noDataFound')}</Typography>
      ) : (
        <Grid container spacing={3}>
          {paymentsSafe.map((payment) => (
            <Grid item xs={12} sm={6} md={4} key={payment.paymentId}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Payment #{payment.paymentId}</Typography>
                  <Typography color="textSecondary">Flat: {payment.flatId}</Typography>
                  <Typography variant="body2">
                    Amount: ${payment.amount} | Type: {payment.paymentType}
                  </Typography>
                  <Typography variant="body2">
                    Method: {payment.paymentMethod}
                  </Typography>
                  <Chip 
                    label={payment.status} 
                    color={getStatusColor(payment.status)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                  {payment.status === 'PENDING' && (
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ mt: 2, minHeight: isMobile ? 44 : 'auto', fontSize: isMobile ? '14px' : '16px' }}
                      onClick={() => handlePayNow(payment)}
                    >
                      Pay Now
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ 
          position: isMobile ? 'sticky' : 'static',
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.paper',
          borderBottom: isMobile ? 1 : 0,
          borderColor: 'divider'
        }}>
          Add New Payment
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField label="Flat ID" value={formData.flatId} onChange={e => setFormData({ ...formData, flatId: e.target.value })} required fullWidth />
              <TextField label="Amount" type="number" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} required fullWidth />
              <TextField label="Payment Type" value={formData.paymentType} onChange={e => setFormData({ ...formData, paymentType: e.target.value })} required fullWidth />
              <TextField label="Payment Method" value={formData.paymentMethod} onChange={e => setFormData({ ...formData, paymentMethod: e.target.value })} required fullWidth />
              <TextField label="Status" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} required fullWidth select SelectProps={{ native: true }}>
                <option value="PENDING">Pending</option>
                <option value="COMPLETED">Completed</option>
                <option value="FAILED">Failed</option>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            position: isMobile ? 'sticky' : 'static',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: isMobile ? 1 : 0,
            borderColor: 'divider',
            p: isMobile ? 2 : 1
          }}>
            <Button onClick={handleClose} sx={{ minHeight: isMobile ? 44 : 'auto' }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={createMutation.isLoading} sx={{ minHeight: isMobile ? 44 : 'auto' }}>
              {createMutation.isLoading ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Payments;